const normalizeDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const dmy = trimmed.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (dmy) {
    const [, dd, mm, yyyy] = dmy;
    return `${yyyy}-${mm}-${dd}`;
  }
  const ymd = trimmed.match(/^(\d{4})[./-](\d{2})[./-](\d{2})$/);
  if (ymd) {
    const [, yyyy, mm, dd] = ymd;
    return `${yyyy}-${mm}-${dd}`;
  }
  return trimmed;
};

const normalizeTime = (value) => {
  if (!value) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?$/.test(trimmed)) return trimmed;
  const hhmm = trimmed.match(/^(\d{1,2})[.:](\d{2})$/);
  if (hhmm) {
    const [, hh, mm] = hhmm;
    return `${hh.padStart(2, '0')}:${mm}:00`;
  }
  return trimmed;
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;
  const normalized = `${value}`.replace(/\s/g, '').replace(',', '.');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapBunuri = (bunuri) => {
  if (!Array.isArray(bunuri)) return [];
  return bunuri
    .map((bun, index) => {
      if (!bun) return null;
      const specificatie = bun.specificatie || bun.denumire || '';
      if (!specificatie.trim()) return null;
      const cantitate = toNumber(bun.cantitate_livrata ?? bun.cantitate);
      const pret = toNumber(bun.pret_unitar_fara_tva ?? bun.pret);
      const valoare = toNumber(bun.valoare) ?? (cantitate !== null && pret !== null ? cantitate * pret : null);

      return {
        nr_crt: bun.nr_crt ?? index + 1,
        specificatie,
        um: bun.um || bun.unitate_masura || '',
        cantitate_livrata: cantitate,
        pret_unitar_fara_tva: pret,
        valoare
      };
    })
    .filter(Boolean);
};

const dropEmpty = (value, { emptyBehavior = 'null' } = {}) => {
  if (value === undefined) return undefined;
  if (value === '') return emptyBehavior === 'omit' ? undefined : null;
  return value;
};

export const normalizeAvizForAPI = (avizData = {}, { mode = 'create' } = {}) => {
  const emptyBehavior = mode === 'update' ? 'omit' : 'null';
  const payload = {
    seria: dropEmpty(avizData.seria, { emptyBehavior }),
    numar: dropEmpty(avizData.numar, { emptyBehavior }),
    data_emitere: normalizeDate(avizData.data_emitere),
    mentiune_scop: dropEmpty(avizData.mentiune_scop, { emptyBehavior }),
    furnizor_denumire: dropEmpty(avizData.furnizor_denumire, { emptyBehavior }),
    furnizor_adresa: dropEmpty(avizData.furnizor_adresa, { emptyBehavior }),
    furnizor_cif: dropEmpty(avizData.furnizor_cif, { emptyBehavior }),
    furnizor_detalii: avizData.furnizor_detalii,
    cumparator_denumire: dropEmpty(avizData.cumparator_denumire, { emptyBehavior }),
    cumparator_adresa: dropEmpty(avizData.cumparator_adresa, { emptyBehavior }),
    cumparator_cif: dropEmpty(avizData.cumparator_cif, { emptyBehavior }),
    cumparator_detalii: avizData.cumparator_detalii,
    bunuri: mapBunuri(avizData.bunuri),
    valoare_totala: toNumber(avizData.valoare_totala),
    moneda: dropEmpty(avizData.moneda, { emptyBehavior }),
    transportator_nume: dropEmpty(avizData.transportator_nume, { emptyBehavior }),
    transportator_ci_serie: dropEmpty(avizData.transportator_ci_serie, { emptyBehavior }),
    transportator_ci_numar: dropEmpty(avizData.transportator_ci_numar, { emptyBehavior }),
    numar_mijloc_transport: dropEmpty(avizData.numar_mijloc_transport, { emptyBehavior }),
    ora_estimativa_livrare: normalizeTime(avizData.ora_estimativa_livrare),
    expeditor_semnatura: avizData.expeditor_semnatura ?? false,
    expeditor_stampila: avizData.expeditor_stampila ?? false,
    transportator_semnatura: avizData.transportator_semnatura ?? false,
    primire: avizData.primire
      ? {
        data_primire_gestiune: normalizeDate(avizData.primire.data_primire_gestiune),
        ora_primire_gestiune: normalizeTime(avizData.primire.ora_primire_gestiune),
        semnatura_primire: avizData.primire.semnatura_primire ?? false,
        primit_de_nume: dropEmpty(avizData.primire.primit_de_nume, { emptyBehavior })
      }
      : undefined
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && (mode !== 'update' || value !== null))
  );
};

export default normalizeAvizForAPI;
